import { useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useBusinessDirectory, useDirectoryFilters } from "@/lib/api/useBusinessDirectory";
import { BusinessGrid, DirectoryFilters, FilterState } from "@/components/directory";
import { Search, X } from "lucide-react";
import SpliceLogo from "@/components/icons/SpliceLogo";
import { buildHomeOpenGraph } from "@/lib/openGraph";
import { bookingSiteOrigin } from "@/lib/siteUrl";
import OpenGraphTags from "@/components/seo/OpenGraphTags";

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [filterState, setFilterState] = useState<FilterState>({
    specialties: [],
    cities: [],
    states: [],
    minRating: null,
  });

  const { filters, loading: filtersLoading } = useDirectoryFilters();

  const { businesses, loading, loadingMore, hasMore, loadMore } =
    useBusinessDirectory({
      search: searchQuery || undefined,
      specialty: filterState.specialties.length > 0 ? filterState.specialties.join(",") : undefined,
      city: filterState.cities.length > 0 ? filterState.cities.join(",") : undefined,
      state: filterState.states.length > 0 ? filterState.states.join(",") : undefined,
      min_rating: filterState.minRating || undefined,
      per_page: 12,
    });

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    },
    []
  );

  const handleSearch = useCallback(() => {
    setSearchQuery(searchValue);
  }, [searchValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    setSearchQuery("");
  }, []);

  const origin = bookingSiteOrigin();
  const openGraph = buildHomeOpenGraph(origin);

  return (
    <>
      <OpenGraphTags {...openGraph} />
      <Helmet>
        <meta
          name="keywords"
          content="book appointment, hair salon near me, nail salon, spa near me, makeup artist, barber shop, beauty salon, wellness center, facials, manicure, pedicure, braids, waxing, massage, Lagos salon, Abuja spa, Nigeria beauty, online booking"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Splice" />
        
        {/* Structured Data for Local Business Directory */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Splice",
            "url": `${origin}/`,
            "description": "Find and book appointments at top-rated beauty and wellness businesses",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${origin}/?search={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-grey-50">
        {/* Subtle Menu Bar with Integrated Search */}
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-grey-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16 gap-4">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <SpliceLogo className="h-6 w-auto" />
              </Link>

              {/* Integrated Search Bar - Full Width */}
              <div className="flex-1">
                <div className="relative flex items-center">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                  <input
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Search treatments, salons, or neighborhoods"
                    className="w-full pl-11 pr-24 py-2.5 bg-grey-50 border border-grey-150 rounded-full text-sm text-grey-900 placeholder:text-grey-400 focus:outline-none focus:ring-2 focus:ring-green-300/50 focus:border-green-300 focus:bg-white transition-all"
                  />
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {searchValue && (
                      <button
                        onClick={handleClearSearch}
                        className="p-1.5 hover:bg-grey-200 rounded-full transition-colors"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4 text-grey-500" />
                      </button>
                    )}
                    <button
                      onClick={handleSearch}
                      className="px-4 py-1.5 bg-green-300 hover:bg-green-400 text-white text-sm font-medium rounded-full transition-colors"
                      aria-label="Search"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Simplified */}
        <section className="bg-gradient-to-br from-green-900 to-green-300 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center max-w-2xl mx-auto">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                Book Your Next Appointment
              </h1>
              <p className="text-base sm:text-lg text-green-50/90">
                Discover top-rated beauty and wellness businesses. Book online
                in seconds.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          {/* Filters */}
          <div className="mb-6">
            <DirectoryFilters
              filters={filters}
              value={filterState}
              onChange={setFilterState}
              loading={filtersLoading}
            />
          </div>

          {/* Business Grid */}
          <BusinessGrid
            businesses={businesses}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            searchQuery={searchQuery}
          />
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-grey-100 mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center text-grey-400 text-sm">
              <p>Powered by Splice</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
